import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@et/schema';
import {
  BoldClaimCard, Card, CardBody, EmptyState, ErrorCard, EvidenceGlyph, Skeleton, StrengthTag,
  type ClaimSummary,
} from '@et/ui';
import { fetchTopic, queryKeys, type ContentRev, type TopicDetail } from '../../lib/api.js';
import { localePath } from '../../lib/locale.js';
import { PageMeta } from '../../app/PageMeta.js';
import { TopicProgress } from '../learn/TopicProgress.js';

/**
 * §5 — o topico e a porta de entrada, e o que se ve primeiro sao os claims.
 *
 * Os `foundational` vem em cima porque sao os que a tradicao precisa que sejam
 * verdadeiros; a ordem e da API, para nao haver duas ideias diferentes de
 * "primeiro" entre servidor e cliente.
 */
export function TopicPage({ locale, rev }: { readonly locale: Locale; readonly rev: ContentRev }) {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const q = useQuery({
    queryKey: queryKeys.topic(rev, locale, slug),
    queryFn: () => fetchTopic(rev.version, locale, slug),
  });

  if (q.isPending) return <Card><CardBody><Skeleton width="45%" height="24px" /></CardBody></Card>;
  if (q.isError || q.data === undefined) return <ErrorCard onRetry={() => void q.refetch()} />;

  const d: TopicDetail = q.data;

  return (
    <div className="et-topicpage">
      <PageMeta title={d.title} description={d.summary?.text} />
      <nav className="et-detail__back">
        <Link to={localePath(locale)}>{t('action.back')}</Link>
      </nav>

      <header className="et-topicpage__head">
        <h1 className="et-detail__title">{d.title}</h1>
        {d.summary !== null && <p className="et-prose">{d.summary.text}</p>}
      </header>

      <TopicProgress locale={locale} rev={rev} slug={slug} />

      <section className="et-claimpage__section">
        <h2 className="et-claimpage__heading">{t('topic.claims')}</h2>
        {d.claims.length === 0
          ? <EmptyState title={t('topic.noClaims')} />
          : (
            <ul className="et-topicpage__claims">
              {d.claims.map((c) => (
                <li key={c.id}>
                  <BoldClaimCard
                    claim={c as ClaimSummary}
                    href={localePath(locale, 'claim', c.id)}
                    counterpartTitle={c.counterpartTitle}
                  />
                </li>
              ))}
            </ul>
          )}
      </section>

      {d.evidence.length > 0 && (
        <section className="et-claimpage__section">
          <h2 className="et-claimpage__heading">{t('topic.evidence')}</h2>
          <ul className="et-topicpage__evidence">
            {d.evidence.map((e) => (
              <li key={e.id}>
                <Card>
                  <CardBody>
                    <div className="et-row">
                      <EvidenceGlyph type={e.evidenceType as never} decorative />
                      <div className="et-row__main">
                        <Link to={localePath(locale, 'evidence', e.id)} className="et-row__title">
                          {e.title}
                        </Link>
                      </div>
                      <StrengthTag strength={e.strength as never} />
                    </div>
                  </CardBody>
                </Card>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
